import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Paper, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import {
  fetchActiveValidationLock,
  fetchProjectTagNames,
  fetchValidationQueuePage,
  fetchValidationLocks,
  fetchValidationProjectSummaries,
  fetchTimeeventById,
  releaseActiveValidationLock,
  updateTimeeventValidation,
} from '../Utils/timeEventsApi';
import { useUser, useWebSocket } from './WebSocketContext';
import AddTagDialog from './Timevents/AddTagDialog';
import TimeEventTag from './Timevents/TimeEventTag';
import { getViewerTypeFromFilename, VIEWER_COMPONENTS } from './Viewer/FileViewerProvider';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';

const VALIDATION_QUEUE_PAGE_SIZE = 25;

const normalizeTagVariable = (value) => String(value || '').trim().toLowerCase();

const upsertTag = (tags, incomingTag) => {
  const incomingVariable = normalizeTagVariable(incomingTag?.variable);
  const existingIndex = tags.findIndex((tag) => (
    (incomingTag?.id != null && String(tag?.id) === String(incomingTag.id))
    || (incomingVariable && normalizeTagVariable(tag?.variable) === incomingVariable)
  ));
  if (existingIndex < 0) return [...tags, incomingTag];
  return tags.map((tag, index) => index === existingIndex ? incomingTag : tag);
};

export default function ValidatorWorkspace() {
  const { caseid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isValidator, username } = useUser();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const [projects, setProjects] = useState([]);
  const [projectSummaries, setProjectSummaries] = useState([]);
  const [events, setEvents] = useState([]);
  const [index, setIndex] = useState(0);
  const [event, setEvent] = useState(null);
  const [metadata, setMetadata] = useState('');
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [suggestedTag, setSuggestedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queuePageLoading, setQueuePageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [totalPending, setTotalPending] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [queuePage, setQueuePage] = useState(1);
  const [queueTotalPages, setQueueTotalPages] = useState(1);
  const [projectProcessingCount, setProjectProcessingCount] = useState(0);
  const [validationLock, setValidationLock] = useState({ eventId: null, token: '', status: 'idle' });
  const [validationLocks, setValidationLocks] = useState([]);
  const lastLockHeartbeatRef = useRef(0);
  const currentEventIdRef = useRef(null);

  const currentSummary = events[index] || null;
  currentEventIdRef.current = currentSummary?.id ?? null;
  const viewerType = getViewerTypeFromFilename(event?.eventsource);
  const ViewerComponent = VIEWER_COMPONENTS[viewerType] || VIEWER_COMPONENTS.other;

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const activeLock = await fetchActiveValidationLock();
      if (activeLock?.case_id && activeLock?.event_id) {
        navigate(`/validate/${activeLock.case_id}`, { replace: true });
        return;
      }
      const [payload, summaries] = await Promise.all([
        apiRequest('/api/v1/projects?limit=200'),
        fetchValidationProjectSummaries(),
      ]);
      setProjects(payload?.projects || payload?.cases || payload?.data?.projects || payload?.data?.cases || []);
      setProjectSummaries(summaries);
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.load_projects_error'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  const loadEvents = useCallback(async ({
    page = 1,
    preserveCurrent = false,
    targetIndex = 0,
    resetProgress = false,
  } = {}) => {
    if (!caseid) return;
    setLoading(true);
    setQueuePageLoading(true);
    setError('');
    try {
      const activeLock = await fetchActiveValidationLock();
      if (activeLock?.case_id && Number(activeLock.case_id) !== Number(caseid)) {
        navigate(`/validate/${activeLock.case_id}`, { replace: true });
        return;
      }
      const [queuePayload, summaries] = await Promise.all([
        fetchValidationQueuePage(caseid, {
          page,
          pageSize: VALIDATION_QUEUE_PAGE_SIZE,
        }),
        fetchValidationProjectSummaries(caseid),
      ]);
      let unconfirmed = (queuePayload?.events || queuePayload?.data?.events || [])
        .filter((item) => Number(item?.isConfirmed) !== 1);
      const activeEventId = Number(activeLock?.event_id || 0);
      const activeEventInPage = unconfirmed.some((item) => Number(item?.id) === activeEventId);

      // A validator may resume a locked event that is not on the first page.
      // Keep a lightweight summary for it; the detail request below loads the
      // full event only after the workspace is ready to display it.
      if (page === 1 && activeEventId && !activeEventInPage) {
        unconfirmed = [{ id: activeEventId, case_id: Number(caseid), isConfirmed: 0 }, ...unconfirmed];
      }

      const projectSummary = summaries.find((summary) => Number(summary.case_id) === Number(caseid));
      setEvents(unconfirmed);
      setQueuePage(Number(queuePayload?.currentPage || page));
      setQueueTotalPages(Math.max(Number(queuePayload?.totalPages) || 1, 1));
      const reportedTotal = Number(queuePayload?.totalCount ?? queuePayload?.data?.totalCount);
      const currentTotal = Number.isFinite(reportedTotal) ? reportedTotal : unconfirmed.length;
      if (resetProgress) {
        setTotalPending(currentTotal);
        setProcessedCount(0);
      } else {
        setTotalPending((previousTotal) => Math.max(previousTotal, currentTotal));
      }
      setProjectProcessingCount(Number(projectSummary?.processing_count || 0));
      setIndex((previousIndex) => {
        if (activeEventId && page === 1) {
          const activeIndex = unconfirmed.findIndex((item) => Number(item?.id) === activeEventId);
          if (activeIndex >= 0) return activeIndex;
        }
        if (activeLock?.event_id) {
          const activeIndex = unconfirmed.findIndex(
            (item) => String(item?.id) === String(activeLock.event_id)
          );
          if (activeIndex >= 0) return activeIndex;
        }
        if (preserveCurrent && currentEventIdRef.current != null) {
          const preservedIndex = unconfirmed.findIndex(
            (item) => String(item?.id) === String(currentEventIdRef.current)
          );
          if (preservedIndex >= 0) return preservedIndex;
        }
        if (targetIndex === 'last') return Math.max(unconfirmed.length - 1, 0);
        return Number.isInteger(targetIndex)
          ? Math.min(targetIndex, Math.max(unconfirmed.length - 1, 0))
          : Math.min(previousIndex, Math.max(unconfirmed.length - 1, 0));
      });
      return { events: unconfirmed, totalPages: Math.max(Number(queuePayload?.totalPages) || 1, 1) };
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.load_timeevents_error'));
      return null;
    } finally {
      setQueuePageLoading(false);
      setLoading(false);
    }
  }, [caseid, navigate, t]);

  useEffect(() => {
    if (!isValidator) return;
    if (caseid) loadEvents({ resetProgress: true });
    else loadProjects();
  }, [caseid, isValidator, loadEvents, loadProjects]);

  useEffect(() => {
    if (!caseid || !isValidator) {
      setAvailableTags([]);
      return undefined;
    }
    let active = true;
    fetchProjectTagNames(caseid)
      .then((projectTags) => active && setAvailableTags(projectTags))
      .catch(() => active && setAvailableTags([]));
    return () => { active = false; };
  }, [caseid, isValidator]);

  useEffect(() => subscribeToMessages(['timeeventPostprocessingState'], (message) => {
    const data = message?.data || {};
    const affectedEventIds = Array.isArray(data.timeevent_ids)
      ? data.timeevent_ids.map((eventId) => Number(eventId))
      : [];
    const currentEventId = Number(currentEventIdRef.current);
    const currentEventIsAffected = Boolean(
      caseid
      && Number(data.case_id) === Number(caseid)
      && currentEventId
      && affectedEventIds.includes(currentEventId)
      && ['processing', 'ready', 'ready_with_errors'].includes(data.state)
    );
    if (currentEventIsAffected) {
      setEvent((previous) => previous ? {
        ...previous,
        postprocessing_state: data.state,
        postprocessing_error: data.error || null,
      } : previous);
      if (data.state === 'processing') {
        setValidationLock({ eventId: currentEventId, token: '', status: 'processing' });
      } else {
        setValidationLock({ eventId: currentEventId, token: '', status: 'acquiring' });
        sendMessage({ command: 'validation_lock_acquire', event_id: currentEventId });
      }
    } else if (caseid && Number(data.case_id) === Number(caseid)) {
      loadEvents({ page: queuePage, preserveCurrent: true });
    } else if (!caseid) {
      loadProjects();
    }
  }), [caseid, loadEvents, loadProjects, queuePage, sendMessage, subscribeToMessages]);

  useEffect(() => {
    if (!caseid || !isValidator) return undefined;
    const refreshLocks = () => fetchValidationLocks(caseid).then(setValidationLocks).catch(() => {});
    refreshLocks();
    const timer = window.setInterval(refreshLocks, 15000);
    return () => window.clearInterval(timer);
  }, [caseid, isValidator]);

  useEffect(() => {
    if (!currentSummary) {
      setEvent(null);
      return;
    }
    let active = true;
    setAddTagDialogOpen(false);
    setLoading(true);
    fetchTimeeventById(currentSummary.id)
      .then((loadedEvent) => {
        if (!active) return;
        setEvent(loadedEvent);
        setMetadata(loadedEvent?.eventdata == null ? '' : String(loadedEvent.eventdata));
        setTags(Array.isArray(loadedEvent?.customFields) ? loadedEvent.customFields : []);
      })
      .catch((requestError) => active && setError(requestError?.message || t('validation_workspace.load_timeevent_error')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currentSummary, t]);

  useEffect(() => subscribeToMessages(['validation_lock', 'validation_lock_heartbeat'], (message) => {
    const data = message?.data || {};
    if (message.command === 'validation_lock' && Number(data.event_id) === Number(currentSummary?.id)) {
      lastLockHeartbeatRef.current = data.acquired ? Date.now() : 0;
      setValidationLock({
        eventId: Number(data.event_id),
        token: data.acquired ? String(data.lock_token || '') : '',
        status: data.acquired ? 'held' : 'unavailable',
      });
      if (!data.acquired) {
        if (data.code === 'validation_lock_active' && data.active_event_id) {
          if (data.active_case_id && Number(data.active_case_id) !== Number(caseid)) {
            navigate(`/validate/${data.active_case_id}`, { replace: true });
          } else {
            const activeIndex = events.findIndex(
              (item) => String(item?.id) === String(data.active_event_id)
            );
            if (activeIndex >= 0) setIndex(activeIndex);
          }
          return;
        }
        const remainingEvents = events.filter((item) => Number(item.id) !== Number(data.event_id));
        setEvents(remainingEvents);
        setIndex((previous) => Math.min(previous, Math.max(0, remainingEvents.length - 1)));
        if (remainingEvents.length === 0 && queuePage < queueTotalPages) {
          loadEvents({ page: queuePage + 1, targetIndex: 0 });
        }
      }
    }
    if (message.command === 'validation_lock_heartbeat' && Number(data.event_id) === Number(validationLock.eventId)) {
      if (data.renewed) {
        lastLockHeartbeatRef.current = Date.now();
      } else {
        lastLockHeartbeatRef.current = 0;
        setValidationLock((previous) => ({ ...previous, token: '', status: 'expired' }));
      }
    }
  }), [caseid, currentSummary?.id, events, loadEvents, navigate, queuePage, queueTotalPages, subscribeToMessages, validationLock.eventId]);

  useEffect(() => {
    if (!currentSummary?.id || !isValidator) return;
    lastLockHeartbeatRef.current = 0;
    setValidationLock({ eventId: Number(currentSummary.id), token: '', status: 'acquiring' });
    sendMessage({ command: 'validation_lock_acquire', event_id: Number(currentSummary.id) });
  }, [currentSummary?.id, isValidator, sendMessage]);

  useEffect(() => {
    if (validationLock.status !== 'held' || !validationLock.token) return undefined;
    const timer = window.setInterval(() => {
      sendMessage({ command: 'validation_lock_heartbeat', event_id: validationLock.eventId, lock_token: validationLock.token });
    }, 30000);
    return () => window.clearInterval(timer);
  }, [sendMessage, validationLock]);

  useEffect(() => {
    if (validationLock.status !== 'held') return undefined;
    const timer = window.setInterval(() => {
      const heartbeatIsMissing = Date.now() - lastLockHeartbeatRef.current > 45000;
      if (document.visibilityState === 'visible' && heartbeatIsMissing) {
        setValidationLock((previous) => ({ ...previous, token: '', status: 'expired' }));
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [validationLock.status]);

  useEffect(() => () => {
    if (validationLock.token && validationLock.eventId) {
      sendMessage({ command: 'validation_lock_release', event_id: validationLock.eventId, lock_token: validationLock.token });
    }
  }, [sendMessage, validationLock.eventId, validationLock.token]);

  const save = useCallback(async () => {
    if (!event) return false;
    setSaving(true);
    setError('');
    try {
      if (metadata !== String(event.eventdata ?? '')) {
        await updateTimeeventValidation(event.id, { metadata }, { lockToken: validationLock.token });
      }
      // TimeEventTag persists only the field the validator actually edits.
      // Do not resend the full tag snapshot here: it creates duplicate audit
      // records and can overwrite a newer value with stale workspace state.
      setEvent((previous) => ({ ...previous, eventdata: metadata, customFields: tags }));
      return true;
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.save_error'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [event, metadata, t, tags, validationLock.token]);

  const confirmAndNext = useCallback(async () => {
    if (!await save()) return;
    setSaving(true);
    try {
      await updateTimeeventValidation(event.id, { confirm: true }, { lockToken: validationLock.token });
      setProcessedCount((previous) => previous + 1);
      const isLastLoadedEvent = index >= events.length - 1;
      setEvents((previous) => previous.filter((item) => item.id !== event.id));
      if (isLastLoadedEvent) {
        // Offset-based pagination shifts after a confirmation. Reload page 1
        // only when the local page is exhausted so no queue item is skipped.
        await loadEvents({ page: 1, targetIndex: 0 });
      }
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.confirm_error'));
    } finally {
      setSaving(false);
    }
  }, [event, events.length, index, loadEvents, save, t, validationLock.token]);

  const moveToPrevious = useCallback(async () => {
    if (saving || queuePageLoading) return;
    if (index > 0) {
      setIndex((currentIndex) => currentIndex - 1);
      return;
    }
    if (queuePage > 1) {
      await loadEvents({ page: queuePage - 1, targetIndex: 'last' });
    }
  }, [index, loadEvents, queuePage, queuePageLoading, saving]);

  const moveToNext = useCallback(async () => {
    if (saving || queuePageLoading) return;
    if (index < events.length - 1) {
      setIndex((currentIndex) => currentIndex + 1);
      return;
    }
    if (queuePage < queueTotalPages) {
      await loadEvents({ page: queuePage + 1, targetIndex: 0 });
    }
  }, [events.length, index, loadEvents, queuePage, queueTotalPages, queuePageLoading, saving]);

  const leaveValidationEvent = useCallback(async () => {
    if (saving) return;
    const hasUnsavedMetadata = event && metadata !== String(event.eventdata ?? '');
    if (hasUnsavedMetadata && !window.confirm(t('validation_workspace.leave_event_confirm'))) return;

    setSaving(true);
    setError('');
    try {
      if (validationLock.status === 'held' && validationLock.token) {
        await releaseActiveValidationLock(validationLock.token);
      }
      setValidationLock({ eventId: null, token: '', status: 'idle' });
      navigate('/validate', { replace: true });
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.leave_event_error'));
    } finally {
      setSaving(false);
    }
  }, [event, metadata, navigate, saving, t, validationLock.status, validationLock.token]);

  useEffect(() => {
    const onKeyDown = (keyboardEvent) => {
      const targetTag = String(keyboardEvent.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(targetTag) || keyboardEvent.metaKey || keyboardEvent.ctrlKey || keyboardEvent.altKey) return;
      if (keyboardEvent.key === 'ArrowLeft') moveToPrevious();
      if (keyboardEvent.key === 'ArrowRight') moveToNext();
      if (keyboardEvent.key.toLowerCase() === 'c') confirmAndNext();
      if (keyboardEvent.key.toLowerCase() === 's') save();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmAndNext, moveToNext, moveToPrevious, save]);

  const validationProgress = useMemo(() => {
    return {
      processedCount,
      value: totalPending ? (processedCount / totalPending) * 100 : 0,
    };
  }, [processedCount, totalPending]);
  const validationLocksForDisplay = useMemo(() => {
    const locksByEventId = new Map(validationLocks.map((lock) => [Number(lock.event_id), lock]));
    if (validationLock.status === 'held' && validationLock.eventId && !locksByEventId.has(Number(validationLock.eventId))) {
      locksByEventId.set(Number(validationLock.eventId), {
        event_id: validationLock.eventId,
        username,
      });
    }
    return Array.from(locksByEventId.values());
  }, [username, validationLock.eventId, validationLock.status, validationLocks]);
  const suggestedTags = useMemo(() => {
    const existingVariables = new Set(
      tags.map((tag) => normalizeTagVariable(tag?.variable)).filter(Boolean)
    );
    const seenVariables = new Set();
    return availableTags.filter((tag) => {
      const variable = normalizeTagVariable(tag?.variable);
      if (!variable || existingVariables.has(variable) || seenVariables.has(variable)) return false;
      seenVariables.add(variable);
      return true;
    });
  }, [availableTags, tags]);
  const projectSummaryById = useMemo(() => new Map(
    projectSummaries.map((summary) => [Number(summary.case_id), summary])
  ), [projectSummaries]);
  const totalProjectsPending = useMemo(() => projectSummaries.reduce(
    (total, summary) => total + Number(summary.pending_count || 0), 0
  ), [projectSummaries]);
  const totalProjectsProcessing = useMemo(() => projectSummaries.reduce(
    (total, summary) => total + Number(summary.processing_count || 0), 0
  ), [projectSummaries]);

  const updateTagInWorkspace = useCallback((updatedTag) => {
    setTags((previous) => previous.map((tag) => tag.id === updatedTag.id ? updatedTag : tag));
    setEvent((previous) => previous ? {
      ...previous,
      customFields: (previous.customFields || []).map((tag) => tag.id === updatedTag.id ? updatedTag : tag),
    } : previous);
  }, []);
  const removeTagFromWorkspace = useCallback((deletedTag) => {
    setTags((previous) => previous.filter((tag) => tag.id !== deletedTag.id));
    setEvent((previous) => previous ? {
      ...previous,
      customFields: (previous.customFields || []).filter((tag) => tag.id !== deletedTag.id),
    } : previous);
  }, []);
  const addTagToWorkspace = useCallback((createdTag) => {
    if (!createdTag) return;
    setTags((previous) => upsertTag(previous, createdTag));
    setEvent((previous) => previous ? {
      ...previous,
      customFields: upsertTag(previous.customFields || [], createdTag),
    } : previous);
    setAvailableTags((previous) => {
      const variable = normalizeTagVariable(createdTag.variable);
      return previous.some((tag) => normalizeTagVariable(tag?.variable) === variable)
        ? previous
        : [...previous, createdTag];
    });
    setSuggestedTag(null);
    setAddTagDialogOpen(false);
  }, []);
  if (!isValidator) return <Typography sx={{ p: 4 }}>{t('validation_workspace.access_denied')}</Typography>;
  if (!caseid) return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, background: 'linear-gradient(135deg, #5757b0, #7777d0)', color: 'primary.contrastText' }}>
        <Typography variant="h5" fontWeight="bold">{t('validation_workspace.inbox_title')}</Typography>
        <Typography sx={{ mt: 0.5, opacity: 0.9 }}>{t('validation_workspace.inbox_subtitle')}</Typography>
        <Typography variant="h3" fontWeight="bold" sx={{ mt: 2 }}>{totalProjectsPending}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('validation_workspace.pending_total')}</Typography>
        {totalProjectsProcessing > 0 && <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          {t('validation_workspace.processing_total', { count: totalProjectsProcessing })}
        </Typography>}
      </Paper>
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : totalProjectsPending === 0 && totalProjectsProcessing === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 44 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>{t('validation_workspace.inbox_empty_title')}</Typography>
          <Typography color="text.secondary">{t('validation_workspace.inbox_empty_description')}</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 2 }}>
          {projects.map((project) => {
            const summary = projectSummaryById.get(Number(project.id)) || {};
            const pending = Number(summary.pending_count || 0);
            const total = Number(summary.total_count || 0);
            const locked = Number(summary.locked_count || 0);
            const processing = Number(summary.processing_count || 0);
            if (!pending && !processing) return null;
            const progressValue = total ? (Math.max(total - pending - processing, 0) / total) * 100 : 0;
            return <Paper key={project.id} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="bold" noWrap>{project.title || project.name || t('validation_workspace.project_fallback', { id: project.id })}</Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">{pending}</Typography>
              <Typography variant="body2" color="text.secondary">{t('validation_workspace.pending_in_project')}</Typography>
              <LinearProgress variant="determinate" value={progressValue} sx={{ height: 7, borderRadius: 4 }} />
              {locked > 0 && <Typography variant="caption" color="text.secondary">{t('validation_workspace.being_validated', { count: locked })}</Typography>}
              {processing > 0 && <Typography variant="caption" color="primary.main">{t('validation_workspace.processing_in_project', { count: processing })}</Typography>}
              <Button variant="contained" disabled={!pending} onClick={() => navigate(`/validate/${project.id}`)} sx={{ mt: 'auto' }}>
                {pending ? t('validation_workspace.start_validation') : t('validation_workspace.waiting_for_processing')}
              </Button>
            </Paper>;
          })}
        </Box>
      )}
    </Box>
  );
  if (!loading && !event) return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 4 }}>
        {projectProcessingCount > 0 ? <>
          <CircularProgress size={26} />
          <Typography variant="h6" sx={{ mt: 2 }}>{t('validation_workspace.processing_title')}</Typography>
          <Typography sx={{ mt: 1 }}>{t('validation_workspace.processing_description', { count: projectProcessingCount })}</Typography>
        </> : <>
          <Typography variant="h6">{t('validation_workspace.complete_title')}</Typography>
          <Typography sx={{ mt: 1 }}>{t('validation_workspace.complete_summary', { count: totalPending })}</Typography>
        </>}
      </Paper>
    </Box>
  );

  return <Box sx={{
    p: 1,
    minHeight: '100dvh',
    height: { xs: 'auto', lg: '100dvh' },
    boxSizing: 'border-box',
    overflow: { xs: 'auto', lg: 'hidden' },
    background: '#f7f8fc',
  }}>
    <Paper sx={{ px: 1.25, py: 0.75, mb: 1, display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: 180, width: { xs: '100%', md: 'auto' }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>{t('validation_workspace.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, maxWidth: 240 }}>
          <LinearProgress
            variant="determinate"
            value={validationProgress.value}
            aria-label={t('validation_workspace.progress_aria')}
            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {validationProgress.processedCount} / {totalPending}
          </Typography>
        </Box>
        {validationLocksForDisplay.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {t('validation_workspace.in_progress')}:
          </Typography>
          {validationLocksForDisplay.map((lock) => {
            const isCurrentUser = String(lock.username || '').toLowerCase() === String(username || '').toLowerCase();
            return (
            <Box
              key={lock.event_id}
              title={lock.username}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.65, py: 0.2, borderRadius: 1,
                bgcolor: isCurrentUser ? 'primary.main' : 'grey.100',
                color: isCurrentUser ? 'primary.contrastText' : 'text.secondary',
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 15, color: 'inherit' }} aria-label={lock.username} />
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: isCurrentUser ? 700 : 500 }}>
                {isCurrentUser ? t('validation_workspace.you') : lock.username} {'\u00B7'} #{lock.event_id}
              </Typography>
            </Box>
            );
          })}
        </Box>}
      </Box>
      <Button
        startIcon={<ArrowBackIcon />}
        disabled={saving}
        onClick={leaveValidationEvent}
        sx={{ flex: { xs: '1 1 100%', md: '0 0 auto' }, minHeight: { xs: 44, md: 36 }, order: { xs: 4, md: 0 } }}
      >
        {t('validation_workspace.leave_event')}
      </Button>
      <Button sx={{ flex: { xs: 1, md: '0 0 auto' }, minHeight: { xs: 44, md: 36 } }} startIcon={<NavigateBeforeIcon />} disabled={(index === 0 && queuePage <= 1) || saving || queuePageLoading} onClick={moveToPrevious}>{t('validation_workspace.previous')}</Button>
      <Button sx={{ flex: { xs: 1, md: '0 0 auto' }, minHeight: { xs: 44, md: 36 } }} endIcon={<NavigateNextIcon />} disabled={(index >= events.length - 1 && queuePage >= queueTotalPages) || saving || queuePageLoading} onClick={moveToNext}>{t('validation_workspace.next')}</Button>
    </Paper>
    <Dialog
      open={validationLock.status === 'expired'}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xs"
      aria-labelledby="validation-lock-expired-title"
      sx={{ zIndex: 10000 }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(15, 23, 42, 0.72)' },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: '50%',
          color: 'warning.dark',
          bgcolor: 'warning.light',
        }}>
          <WarningAmberRoundedIcon fontSize="large" />
        </Box>
      </Box>
      <DialogTitle id="validation-lock-expired-title" sx={{ pb: 1, textAlign: 'center', fontWeight: 700 }}>
        {t('validation_workspace.title')}
      </DialogTitle>
      <DialogContent sx={{ pt: 1, textAlign: 'center' }}>
        <Alert severity="warning" variant="outlined" sx={{ textAlign: 'left', borderRadius: 2 }}>
          {t('validation_workspace.lock_expired')}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t('validation_workspace.reload')}
        </Button>
      </DialogActions>
    </Dialog>
    {validationLock.status === 'processing' && (
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('validation_workspace.event_processing')}
      </Alert>
    )}
    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
    {event?.postprocessing_state === 'ready_with_errors' && (
      <Alert severity="warning" sx={{ mb: 1 }}>
        {event.postprocessing_error || t('validation_workspace.postprocessing_warning')}
      </Alert>
    )}
    {loading ? <CircularProgress /> : !event ? <Paper sx={{ p: 4 }}><Typography variant="h6">{t('validation_workspace.complete_title')}</Typography><Typography sx={{ mt: 1 }}>{t('validation_workspace.complete_summary', { count: totalPending })}</Typography></Paper> :
      <Box sx={{
        display: 'grid',
        height: { xs: 'auto', lg: 'calc(100dvh - 62px)' },
        minHeight: 0,
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(560px, 65%) minmax(320px, 35%)' },
        gridTemplateRows: { xs: 'minmax(280px, 45dvh) auto', lg: 'minmax(0, 1fr)' },
        gap: 1,
      }}>
        <Paper sx={{ minHeight: { xs: 280, lg: 0 }, height: { xs: '45dvh', lg: '100%' }, overflow: 'hidden', p: 0 }}>
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>}>
            <ViewerComponent
              open
              embedded
              onClose={() => {}}
              filename={String(event.eventsource || '').split(/[\\/]/).pop()}
              eventId={event.id}
              filePath={event.eventsource}
              timeevent={event}
            />
          </React.Suspense>
        </Paper>
        <Paper sx={{ minHeight: 0, overflow: { xs: 'visible', lg: 'auto' }, p: 1.5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('validation_workspace.tags_title')}
          </Typography>
          {validationLock.status !== 'held' && <Typography variant="caption" color="warning.main">
            {validationLock.status === 'unavailable'
              ? t('validation_workspace.locked_by', {
                  username: validationLocks.find((lock) => Number(lock.event_id) === Number(event?.id))?.username
                    || t('validation_workspace.other_user'),
                })
              : validationLock.status === 'processing'
                ? t('validation_workspace.event_processing')
              : t('validation_workspace.locking')}
          </Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('validation_workspace.tags_help')}
          </Typography>
          <Box sx={{ minHeight: 360, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
            {tags.map((tag, tagIndex) => (
              <Box key={tag.id || tagIndex} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeEventTag tag={tag} timeevent={event} canManageProject={validationLock.status === 'held'} showFullValue validationLockToken={validationLock.token} openViewerOnHover={false} onUpdate={updateTagInWorkspace} onDelete={removeTagFromWorkspace} />
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
              disabled={saving || validationLock.status !== 'held'}
              onClick={() => {
                setSuggestedTag(null);
                setAddTagDialogOpen(true);
              }}
              aria-label={t('validation_workspace.add_tag')}
              title={t('validation_workspace.add_tag_hint')}
              sx={{
                width: { xs: '100%', sm: 'fit-content' },
                minHeight: { xs: 44, sm: 36 },
                mt: 0.5,
                px: 1.25,
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderStyle: 'dashed',
                borderWidth: '1.5px',
                borderRadius: '10px',
                borderColor: 'primary.main',
                color: 'primary.main',
                backgroundColor: 'rgba(87, 87, 176, 0.04)',
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: '1.5px',
                  backgroundColor: 'rgba(87, 87, 176, 0.1)',
                },
                '& .MuiButton-startIcon': {
                  mr: 0.75,
                },
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {t('validation_workspace.add_tag')}
            </Button>
            {suggestedTags.length > 0 && <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                  {t('validation_workspace.suggested_tags')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                {suggestedTags.map((tag) => {
                  const tagVariable = String(tag.variable || '').trim();
                  const tagPastelColor = generatePastelColor(tagVariable);
                  const tagFontColor = generateSaturatedColor(tagVariable);
                  return (
                    <Button
                      key={tag.id || tag.variable}
                      size="small"
                      variant="text"
                      disabled={saving || validationLock.status !== 'held'}
                      onClick={() => {
                        setSuggestedTag(tag);
                        setAddTagDialogOpen(true);
                      }}
                      title={tag.description || tagVariable}
                      sx={{
                        justifyContent: 'center',
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        minHeight: 32,
                        width: 'fit-content',
                        maxWidth: '100%',
                        px: 1,
                        border: `1px solid ${tagFontColor}`,
                        borderLeft: `3px solid ${tagFontColor}`,
                        borderRadius: 1.5,
                        bgcolor: tagPastelColor,
                        color: tagFontColor,
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: tagPastelColor,
                          color: tagFontColor,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {tagVariable}
                    </Button>
                  );
                })}
              </Box>
            </Box>}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button startIcon={<SaveIcon />} disabled={saving || validationLock.status !== 'held'} onClick={save}>{t('validation_workspace.save')}</Button>
            <Button variant="contained" startIcon={<CheckCircleIcon />} disabled={saving || validationLock.status !== 'held'} onClick={confirmAndNext}>{t('validation_workspace.confirm_next')}</Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">{t('validation_workspace.information')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField label={t('validation_workspace.metadata')} value={metadata} onChange={(e) => setMetadata(e.target.value)} multiline minRows={5} fullWidth />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Box>}
    {event && <AddTagDialog
      open={addTagDialogOpen}
      onClose={() => {
        setSuggestedTag(null);
        setAddTagDialogOpen(false);
      }}
      onTagCreated={addTagToWorkspace}
      timeevent={event}
      availableTags={availableTags}
      existingTags={tags}
      initialTag={suggestedTag}
      validationLockToken={validationLock.token}
    />}
  </Box>;
}
